/**
 * Channel Service
 *
 * Service functions for interacting with channel-related API endpoints.
 */

import { USER_ENDPOINTS } from '../config/api';
import { ChannelRSSResponse, YouTubeVideo } from '../types/api.types';

/**
 * Fetches RSS feeds for a group of YouTube channels by group ID
 * @param groupId The ID of the group containing the channels
 * @returns Promise containing the group's RSS response
 */
export async function fetchGroupRSS(groupId: string): Promise<ChannelRSSResponse> {
  const url = new URL(USER_ENDPOINTS.CHANNELS_RSS);
  url.searchParams.append('groupId', groupId);

  const response = await fetch(url.toString(), {
    method: 'GET',
    credentials: 'include', // Include cookies for authentication
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Failed to fetch group RSS feeds: ${error}`);
  }

  const data: ChannelRSSResponse = await response.json();
  return data;
}

/**
 * Legacy function for backward compatibility - fetches RSS feeds for multiple YouTube channels
 * @param channelIds Array of YouTube channel IDs
 * @returns Promise containing array of channel RSS responses
 * @deprecated Use fetchGroupRSS instead with group-based organization
 */
export async function fetchChannelRSS(): Promise<ChannelRSSResponse> {
  // This is a legacy function - in practice, users should create groups and use fetchGroupRSS
  // For now, we'll throw an error to encourage migration to the new group-based approach
  throw new Error(
    'Direct channel RSS fetching is no longer supported. Please organize channels into groups and use fetchGroupRSS.',
  );
}

/**
 * Fetches videos and shorts for a group and returns them sorted by publish date
 * @param groupId The ID of the group to fetch videos for
 * @returns Promise containing arrays of videos and shorts sorted by publishedAt (desc) and metadata
 */
export async function fetchGroupVideos(groupId: string): Promise<{
  videos: YouTubeVideo[];
  shorts: YouTubeVideo[];
  failedChannels: number;
  errors: string[];
}> {
  if (!groupId) {
    return {
      videos: [],
      shorts: [],
      failedChannels: 0,
      errors: [],
    };
  }

  const { videos, shorts, errors } = await fetchGroupRSS(groupId);

  return {
    videos: videos,
    shorts: shorts,
    failedChannels: errors?.length || 0,
    errors: errors || [],
  };
}

/**
 * Helper to build YouTube video URL from video ID
 * @param videoId YouTube video ID
 * @returns Full YouTube video URL
 */
export function getYouTubeVideoUrl(videoId: string): string {
  return `https://www.youtube.com/watch?v=${videoId}`;
}

/**
 * Helper to build YouTube channel URL from channel ID
 * @param channelId YouTube channel ID
 * @returns Full YouTube channel URL
 */
export function getYouTubeChannelUrl(channelId: string, customUrl?: string): string {
  return customUrl ? `https://www.youtube.com/${customUrl}` : `https://www.youtube.com/channel/${channelId}`;
}
